import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["open", "onDismiss", "onEscape", "unmountChildrenOnClose", "ignoreEscape", "ignoreBackdropClick", "hideBackdrop", "className", "backdropClassName", "backdropStyles", "children", "isDark", "dark", "position", "disabledFocusLock", "disabledScrollLock", "width", "htmlProps"];
import { Fragment, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';
import cn from 'classnames';
import styled from 'styled-components';
import { useIsClient, useMountChildrenValidation } from '@crehana/react-hooks';
import { KEY_NAMES } from "../helpers/constants";
import transition from "../styles/transition";
import { Position } from "./types";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var DrawerContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-15w9wai-0"
})(["\n  width: ", ";\n  ", " {\n    width: ", ";\n  }\n  ", " {\n    width: ", ";\n  }\n  ", " {\n    width: ", ";\n  }\n"], function (_ref) {
  var width = _ref.width;
  return width.xs;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.breakpoints.up('sm');
}, function (_ref3) {
  var width = _ref3.width;
  return width.sm;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.breakpoints.up('md');
}, function (_ref5) {
  var width = _ref5.width;
  return width.md;
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.breakpoints.up('lg');
}, function (_ref7) {
  var width = _ref7.width;
  return width.lg;
});

/** @deprecated Use import {Drawer} from @crehana/compass.ui */

var Drawer = function Drawer(_ref8) {
  var _ref8$open = _ref8.open,
    open = _ref8$open === void 0 ? false : _ref8$open,
    onDismiss = _ref8.onDismiss,
    onEscape = _ref8.onEscape,
    unmountChildrenOnClose = _ref8.unmountChildrenOnClose,
    _ref8$ignoreEscape = _ref8.ignoreEscape,
    ignoreEscape = _ref8$ignoreEscape === void 0 ? false : _ref8$ignoreEscape,
    _ref8$ignoreBackdropC = _ref8.ignoreBackdropClick,
    ignoreBackdropClick = _ref8$ignoreBackdropC === void 0 ? false : _ref8$ignoreBackdropC,
    _ref8$hideBackdrop = _ref8.hideBackdrop,
    hideBackdrop = _ref8$hideBackdrop === void 0 ? false : _ref8$hideBackdrop,
    _ref8$className = _ref8.className,
    className = _ref8$className === void 0 ? '' : _ref8$className,
    _ref8$backdropClassNa = _ref8.backdropClassName,
    backdropClassName = _ref8$backdropClassNa === void 0 ? '' : _ref8$backdropClassNa,
    backdropStyles = _ref8.backdropStyles,
    children = _ref8.children,
    isDark = _ref8.isDark,
    dark = _ref8.dark,
    _ref8$position = _ref8.position,
    position = _ref8$position === void 0 ? Position.right : _ref8$position,
    _ref8$disabledFocusLo = _ref8.disabledFocusLock,
    disabledFocusLock = _ref8$disabledFocusLo === void 0 ? true : _ref8$disabledFocusLo,
    disabledScrollLock = _ref8.disabledScrollLock,
    widthProp = _ref8.width,
    htmlProps = _ref8.htmlProps,
    drawerProps = _objectWithoutProperties(_ref8, _excluded);
  var drawerRef = useRef(null);
  var isClient = useIsClient();
  var shouldMountChildren = useMountChildrenValidation({
    isOpen: open,
    unmountChildrenOnClose: unmountChildrenOnClose
  });
  var handleKeyDown = function handleKeyDown(event) {
    if (event.key === KEY_NAMES.ESCAPE) {
      event.stopPropagation();
      if (typeof onEscape === 'function') {
        onEscape(event);
      }
      if (!ignoreEscape && typeof onDismiss === 'function') {
        onDismiss();
      }
    }
  };
  var POSITIONS = _defineProperty(_defineProperty(_defineProperty(_defineProperty({}, Position.bottom, {
    transform: "translateY(".concat(open ? 0 : 100, "%)"),
    absolutePosition: 'left-0 right-0 bottom-0',
    className: ''
  }), Position.top, {
    transform: "translateY(".concat(open ? 0 : -100, "%)"),
    absolutePosition: 'left-0 right-0 top-0',
    className: ''
  }), Position.right, {
    transform: "translateX(".concat(open ? 0 : 100, "%)"),
    absolutePosition: 'right-0 bottom-0 top-0',
    className: 'h-screen'
  }), Position.left, {
    transform: "translateX(".concat(open ? 0 : -100, "%)"),
    absolutePosition: 'left-0 top-0 bottom-0',
    className: 'h-screen'
  });
  useEffect(function () {
    var onClickOutside = function onClickOutside(event) {
      if (drawerRef.current && !drawerRef.current.contains(event.target) && onDismiss) {
        onDismiss();
      }
    };
    if (open) {
      document.addEventListener('click', onClickOutside);
    }
    return function () {
      document.removeEventListener('click', onClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!isClient) {
    return null;
  }
  var defaultWidth = position === 'top' || position === 'bottom' ? '100vw' : {
    xs: '80vw',
    sm: '33vw',
    md: '25vw',
    lg: '25vw'
  };
  var width = widthProp || defaultWidth;
  return /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/_jsxs(Fragment, {
    children: [hideBackdrop ? null : /*#__PURE__*/_jsx("div", {
      className: cn('fixed z-dialog inset-0 bg-base-main-80', open ? 'cui-is-visible' : 'cui-is-hidden', backdropClassName),
      style: backdropStyles,
      onClick: ignoreBackdropClick ? undefined : onDismiss,
      onKeyDown: handleKeyDown,
      tabIndex: -1
    }), /*#__PURE__*/_jsx(FocusLock, {
      disabled: disabledFocusLock || open,
      returnFocus: true,
      children: /*#__PURE__*/_jsx(RemoveScroll, {
        enabled: !disabledScrollLock && open,
        forwardProps: true,
        children: /*#__PURE__*/_jsx(DrawerContainer, {
          width: {
            xs: typeof width === 'string' ? width : width.xs,
            sm: typeof width === 'string' ? width : width.sm,
            md: typeof width === 'string' ? width : width.md,
            lg: typeof width === 'string' ? width : width.lg
          },
          className: cn('fixed z-dialog', POSITIONS[position].className, POSITIONS[position].absolutePosition, isDark || dark ? 'bg-base-dark text-white' : 'bg-white shadow-tooltip text-base-main', open ? 'cui-is-visible' : 'cui-is-hidden'),
          style: {
            transform: POSITIONS[position].transform,
            transition: transition(['transform', 'opacity', 'visibility'])
          },
          ref: drawerRef,
          children: /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread(_objectSpread({
            className: cn("w-full max-h-full focus:outline-none", className)
          }, htmlProps), drawerProps), {}, {
            children: shouldMountChildren && children
          }))
        })
      })
    })]
  }), document.body);
};
export default Drawer;